<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  toRef,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarModal } from '@nora-monorepo/grammar-ui';

import type { GalleryFile } from '#ui-shared/components/gallery/gallery.types';
import {
  getGalleryFileSource,
  getInlineGalleryFileUrl,
} from '#ui-shared/components/gallery/gallery.types';
import GalleryFileCard from '#ui-shared/components/gallery/GalleryFileCard.vue';
import GallerySideBar from '#ui-shared/components/gallery/GallerySideBar.vue';
import { useMediaRetry } from '#ui-shared/composables/useMediaRetry';
import {
  isImage,
  isVideo,
} from '#ui-shared/utils/pretty-print-file-size';

interface GalleryProps {
  file: GalleryFile;
  refreshUrls?: () => void;
}

const props = defineProps<GalleryProps>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const isOpen = ref(false);
/**
 * Grammar clears body overflow when its modal closes, even if a parent modal is still open.
 * Save the existing value before opening the gallery so we can restore the parent's scroll lock.
 */
const previousOverflow = document.body.style.overflow;
const videoRef = ref<HTMLVideoElement | null>(null);
const source = computed(() => getGalleryFileSource(props.file));
const inlineUrl = computed(() => getInlineGalleryFileUrl(props.file));
const sourceRef = toRef(source);
const imageFailed = ref(false);

onMounted(async () => {
  // Grammar ignores isOpen on its first render.
  await nextTick();
  isOpen.value = true;
});
onBeforeUnmount(() => {
  // Restore the original scroll state on unmount
  document.body.style.overflow = previousOverflow;
});

const {
  handleError: handleImageError,
  handleSuccess: handleImageSuccess,
} = useMediaRetry({
  urlRef: sourceRef,
  onRefreshRequest: props.refreshUrls,
});

const {
  isError: isVideoError,
  handleError: handleVideoError,
  handleSuccess: handleVideoSuccess,
} = useMediaRetry({
  urlRef: sourceRef,
  onRefreshRequest: props.refreshUrls,
  videoRef,
});

function onImageError(): void {
  imageFailed.value = true;
  handleImageError();
}

function onImageLoad(): void {
  imageFailed.value = false;
  handleImageSuccess();
}

function openInNewWindow(): void {
  if (inlineUrl.value) {
    window.open(inlineUrl.value, '_blank', 'noopener,noreferrer');
  }
}

/** Let Grammar finish releasing its scroll lock before unmount restores the parent lock. */
async function onClose(): Promise<void> {
  await nextTick();
  emit('close');
}
</script>

<template>
  <GrammarModal
    :is-open="isOpen && Boolean(source)"
    @close="onClose"
  >
    <template #content>
      <div
        v-if="source"
        class="fixed inset-0 bg-[#0A0A0A]/85 flex items-center justify-center"
        @click.self="isOpen = false"
      >
        <GallerySideBar
          :file="file"
          @close="isOpen = false"
        />

        <div
          v-if="isImage(file.mimeType)"
          class="relative h-full flex items-center justify-center"
          @click.self="isOpen = false"
        >
          <img
            v-show="!imageFailed"
            :src="source"
            alt=""
            class="max-w-[95%] max-h-[95%] bg-black"
            @click.stop
            @load="onImageLoad"
            @error="onImageError"
          >
          <GalleryFileCard
            v-if="imageFailed"
            :file="file"
            @open="openInNewWindow"
          />
        </div>

        <div
          v-else-if="isVideo(file.mimeType)"
          class="relative h-full flex items-center justify-center"
          @click.self="isOpen = false"
        >
          <GalleryFileCard
            v-if="isVideoError"
            :file="file"
            @open="openInNewWindow"
          />
          <video
            v-else
            ref="videoRef"
            controls
            autoplay
            class="max-h-[95%] max-w-[95%]"
            @click.stop
            @loadeddata="handleVideoSuccess"
            @error="handleVideoError"
          >
            <source :src="source">
            {{ t('error.videoNotSupported') }}
          </video>
        </div>

        <GalleryFileCard
          v-else
          :file="file"
          @open="openInNewWindow"
        />
      </div>
    </template>
  </GrammarModal>
</template>
